import type { CSSProperties } from 'react';
import type { VendorDisplayFont } from '@shopgnd/shared';

/** Default storefront header color (matches Tailwind brand-700). */
export const DEFAULT_VENDOR_BRAND_COLOR = '#195d40';

export const VENDOR_BRAND_PRESETS = [
  { name: 'ShopGND green', hex: '#195d40' },
  { name: 'Ocean blue', hex: '#0369a1' },
  { name: 'Sunset orange', hex: '#c2410c' },
  { name: 'Royal purple', hex: '#6d28d9' },
  { name: 'Crimson', hex: '#b91c1c' },
  { name: 'Charcoal', hex: '#334155' },
] as const;

export const VENDOR_DISPLAY_FONT_OPTIONS: {
  id: VendorDisplayFont;
  label: string;
  hint: string;
}[] = [
  { id: 'platform', label: 'ShopGND default', hint: 'DM Sans body · Fraunces headings' },
  { id: 'serif', label: 'Classic serif', hint: 'DM Serif Display headings' },
  { id: 'modern', label: 'Clean sans', hint: 'DM Sans throughout' },
];

export function resolveVendorDisplayFont(
  vendor?: { displayFont?: VendorDisplayFont | null },
): VendorDisplayFont {
  const f = vendor?.displayFont;
  return f === 'serif' || f === 'modern' || f === 'platform' ? f : 'platform';
}

/** Scoped class on the public business profile article. */
export function vendorStoreFontClass(
  vendor?: { displayFont?: VendorDisplayFont | null },
): string {
  return `store-font-${resolveVendorDisplayFont(vendor)}`;
}

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0'))
      .join('')
  );
}

function shade(hex: string, amount: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return rgbToHex(rgb[0] + amount, rgb[1] + amount, rgb[2] + amount);
}

/** Validate and normalize a hex brand color, or return undefined. */
export function normalizeBrandColor(value?: string | null): string | undefined {
  if (!value?.trim()) return undefined;
  const rgb = parseHex(value);
  if (!rgb) return undefined;
  return rgbToHex(rgb[0], rgb[1], rgb[2]);
}

export function resolveVendorBrandColor(vendor?: { brandColor?: string | null }): string {
  return normalizeBrandColor(vendor?.brandColor) ?? DEFAULT_VENDOR_BRAND_COLOR;
}

/** Inline style for the vendor storefront header gradient (Tailwind `bg-brand-700` is the fallback). */
export function vendorHeaderGradientStyle(brandColor?: string | null): CSSProperties {
  const base = resolveVendorBrandColor({ brandColor });
  return {
    backgroundColor: base,
    backgroundImage: `linear-gradient(135deg, ${shade(base, -35)} 0%, ${base} 42%, ${shade(base, 18)} 100%)`,
  };
}
